import React from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';
import type { TrustStripItem, TrustStripSettings } from '../../../../services/ecommerceApi';

export const DEFAULT_TRUST_STRIP: TrustStripSettings = {
  items: [
    { value: '1000+', label: 'Aktif İşletme' },
    { value: '50K+', label: 'Aylık İşlem' },
    { value: '99.9%', label: 'Sistem Uptime' },
    { value: '7/24', label: 'Destek Hizmeti' },
  ],
  backgroundColor: 'rgba(30, 64, 175, 0.55)',
  textColor: '#ffffff',
  mutedColor: 'rgba(255,255,255,0.85)',
  style: 'columns',
};

/** Metin renginden etiket (muted) rengi üretir */
export function textColorToMuted(color: string, alpha = 0.82): string {
  const raw = (color || '').trim();
  if (!raw) return `rgba(255,255,255,${alpha})`;
  const hex = raw.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    let h = hex[1];
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  const rgb = raw.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*[\d.]+\s*)?\)$/i);
  if (rgb) return `rgba(${rgb[1]}, ${rgb[2]}, ${rgb[3]}, ${alpha})`;
  return raw;
}

export const normalizeTrustStripSettings = (
  raw?: Partial<TrustStripSettings> | null
): TrustStripSettings => {
  const items = Array.isArray(raw?.items)
    ? raw!.items!
        .map((item) => ({
          value: String(item?.value || ''),
          label: String(item?.label || ''),
        }))
        .filter((item) => item.value || item.label)
    : [];

  return {
    items: items.length ? items.slice(0, 4) : [...(DEFAULT_TRUST_STRIP.items || [])],
    backgroundColor: raw?.backgroundColor || DEFAULT_TRUST_STRIP.backgroundColor,
    textColor: raw?.textColor || DEFAULT_TRUST_STRIP.textColor,
    mutedColor: raw?.mutedColor || DEFAULT_TRUST_STRIP.mutedColor,
    style: raw?.style === 'inline' ? 'inline' : 'columns',
  };
};

interface TrustStripSettingsPanelProps {
  enabled: boolean;
  settings: TrustStripSettings;
  onEnabledChange: (enabled: boolean) => void;
  onSettingsChange: (settings: TrustStripSettings) => void;
  onSave: () => void | Promise<void>;
  saving?: boolean;
  saved?: boolean;
  error?: string | null;
  compact?: boolean;
}

const TrustStripSettingsPanel: React.FC<TrustStripSettingsPanelProps> = ({
  enabled,
  settings,
  onEnabledChange,
  onSettingsChange,
  onSave,
  saving = false,
  saved = false,
  error = null,
  compact = false,
}) => {
  const { t } = useTranslation('salesSystem');
  const trustStrip = normalizeTrustStripSettings(settings);

  const patchItem = (index: number, partial: Partial<TrustStripItem>) => {
    const items = [...(trustStrip.items || [])];
    items[index] = { ...items[index], ...partial };
    onSettingsChange({ ...trustStrip, items });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant={compact ? 'subtitle1' : 'h6'} fontWeight="bold">
          {t('ecommerceHomeSettings.trustStrip.title')}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={(e) => onEnabledChange(e.target.checked)}
              size="small"
            />
          }
          label={t('ecommerceHomeSettings.trustStrip.enabled')}
        />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('ecommerceHomeSettings.trustStrip.hint')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {enabled && (
        <Grid container spacing={2}>
          {(trustStrip.items || []).map((item, index) => (
            <Grid item xs={12} key={`trust-item-${index}`}>
              <Grid container spacing={1.5} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('ecommerceHomeSettings.trustStrip.value')}
                    value={item.value || ''}
                    onChange={(e) => patchItem(index, { value: e.target.value })}
                    placeholder="1000+"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('ecommerceHomeSettings.trustStrip.label')}
                    value={item.label || ''}
                    onChange={(e) => patchItem(index, { label: e.target.value })}
                    placeholder="Aktif İşletme"
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={(trustStrip.items || []).length <= 1}
                    onClick={() => {
                      onSettingsChange({
                        ...trustStrip,
                        items: (trustStrip.items || []).filter((_, i) => i !== index),
                      });
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Grid>
              </Grid>
            </Grid>
          ))}

          {(trustStrip.items || []).length < 4 && (
            <Grid item xs={12}>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() =>
                  onSettingsChange({
                    ...trustStrip,
                    items: [...(trustStrip.items || []), { value: '', label: '' }],
                  })
                }
              >
                {t('ecommerceHomeSettings.trustStrip.addItem')}
              </Button>
            </Grid>
          )}

          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('ecommerceHomeSettings.trustStrip.style')}</InputLabel>
              <Select
                label={t('ecommerceHomeSettings.trustStrip.style')}
                value={trustStrip.style || 'columns'}
                onChange={(e) =>
                  onSettingsChange({
                    ...trustStrip,
                    style: e.target.value === 'inline' ? 'inline' : 'columns',
                  })
                }
              >
                <MenuItem value="columns">{t('ecommerceHomeSettings.trustStrip.styleColumns')}</MenuItem>
                <MenuItem value="inline">{t('ecommerceHomeSettings.trustStrip.styleInline')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label={t('ecommerceHomeSettings.trustStrip.backgroundColor')}
              value={trustStrip.backgroundColor || ''}
              onChange={(e) =>
                onSettingsChange({ ...trustStrip, backgroundColor: e.target.value })
              }
              helperText="Örn. rgba(30,64,175,0.55)"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box
                      component="input"
                      type="color"
                      value={
                        String(trustStrip.backgroundColor || '').startsWith('#')
                          ? trustStrip.backgroundColor
                          : '#1e40af'
                      }
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onSettingsChange({ ...trustStrip, backgroundColor: e.target.value })
                      }
                      sx={{ border: 'none', width: 24, height: 24, p: 0, background: 'none', cursor: 'pointer' }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label={t('ecommerceHomeSettings.trustStrip.textColor')}
              value={trustStrip.textColor || '#ffffff'}
              onChange={(e) => {
                const textColor = e.target.value;
                onSettingsChange({
                  ...trustStrip,
                  textColor,
                  // Etiket rengi metin rengiyle birlikte güncellenir
                  mutedColor: textColorToMuted(textColor),
                });
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box
                      component="input"
                      type="color"
                      value={
                        String(trustStrip.textColor || '').startsWith('#')
                          ? trustStrip.textColor
                          : '#ffffff'
                      }
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const textColor = e.target.value;
                        onSettingsChange({
                          ...trustStrip,
                          textColor,
                          mutedColor: textColorToMuted(textColor),
                        });
                      }}
                      sx={{ border: 'none', width: 24, height: 24, p: 0, background: 'none', cursor: 'pointer' }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label={t('ecommerceHomeSettings.trustStrip.labelColor')}
              value={trustStrip.mutedColor || textColorToMuted(trustStrip.textColor || '#ffffff')}
              onChange={(e) =>
                onSettingsChange({ ...trustStrip, mutedColor: e.target.value })
              }
              helperText={t('ecommerceHomeSettings.trustStrip.labelColorHint')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box
                      component="input"
                      type="color"
                      value={
                        String(trustStrip.mutedColor || '').startsWith('#')
                          ? trustStrip.mutedColor
                          : String(trustStrip.textColor || '').startsWith('#')
                            ? trustStrip.textColor
                            : '#ffffff'
                      }
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onSettingsChange({ ...trustStrip, mutedColor: e.target.value })
                      }
                      sx={{ border: 'none', width: 24, height: 24, p: 0, background: 'none', cursor: 'pointer' }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                mt: 1,
                px: 3,
                py: 2.5,
                borderRadius: 3,
                bgcolor: trustStrip.backgroundColor || 'rgba(30,64,175,0.55)',
                display: 'grid',
                gridTemplateColumns: `repeat(${Math.min((trustStrip.items || []).filter((i) => i.value || i.label).length || 1, 4)}, 1fr)`,
                gap: 2,
                textAlign: 'center',
                border: '1px solid rgba(255,255,255,0.25)',
                backdropFilter: 'blur(8px)',
              }}
            >
              {(trustStrip.items || [])
                .filter((i) => i.value || i.label)
                .map((item, index) => (
                  <Box key={`preview-${index}`}>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: 18, md: 24 },
                        color: trustStrip.textColor || '#ffffff',
                      }}
                    >
                      {item.value}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 13,
                        color:
                          trustStrip.mutedColor ||
                          textColorToMuted(trustStrip.textColor || '#ffffff'),
                      }}
                    >
                      {item.label}
                    </Typography>
                  </Box>
                ))}
            </Box>
          </Grid>
        </Grid>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2 }}>
        <Button variant="contained" onClick={() => void onSave()} disabled={saving}>
          {saving
            ? t('ecommerceHomeSettings.trustStrip.saving')
            : enabled
              ? t('ecommerceHomeSettings.trustStrip.save')
              : t('ecommerceHomeSettings.trustStrip.saveHidden')}
        </Button>
        {saved && (
          <Typography variant="body2" color="success.main">
            {t('ecommerceHomeSettings.trustStrip.saved')}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default TrustStripSettingsPanel;
